import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, IconButton, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon, Delete as DeleteIcon,
  LockReset as ResetIcon, Block as SuspendIcon, CheckCircle as ActivateIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import authService from '../shared/services/authService';
import UserForm from './UserForm';

interface AdminUser {
  id: string;
  username: string;
  email: string;
  is_admin: boolean;
  is_active: boolean;
  role?: string;
}

const isSuper = (u: AdminUser) => u.is_admin || u.role === 'super_admin';

const SuperAdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const all = await authService.getAllUsers(false);
      setUsers((all as AdminUser[]).filter(isSuper));
    } catch (error: any) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to load users');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (u: AdminUser) => {
    try { await authService.toggleUserActiveStatus(u.id); await load(); }
    catch (error: any) { toast.error(error.response?.data?.detail || error.message || 'Failed to update status'); }
  };
  const resetPw = async (u: AdminUser) => {
    const pw = window.prompt(`New password for "${u.username}":`);
    if (!pw) return;
    try { await authService.resetUserPassword(u.id, pw); toast.success('Password reset'); }
    catch (error: any) { toast.error(error.response?.data?.detail || error.message || 'Failed to reset password'); }
  };
  const remove = async (u: AdminUser) => {
    if (!window.confirm(`Delete super admin "${u.username}"?`)) return;
    try { await authService.deleteUser(u.id); await load(); toast.success('Deleted'); }
    catch (error: any) { toast.error(error.response?.data?.detail || error.message || 'Failed to delete'); }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Super Admins</Typography>
        <Box display="flex" gap={1}>
          <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
            Create Super Admin
          </Button>
        </Box>
      </Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.username}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Chip label={u.is_active ? 'Active' : 'Inactive'}
                        color={u.is_active ? 'success' : 'default'} size="small" />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={u.is_active ? 'Deactivate' : 'Activate'}>
                    <IconButton size="small" onClick={() => toggleActive(u)}>
                      {u.is_active ? <SuspendIcon /> : <ActivateIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reset password">
                    <IconButton size="small" onClick={() => resetPw(u)}><ResetIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => remove(u)}><DeleteIcon /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {formOpen && (
        <UserForm
          open={formOpen}
          superAdminOnly
          onClose={(shouldRefresh?: boolean) => {
            setFormOpen(false);
            if (shouldRefresh) load();
          }}
        />
      )}
    </Box>
  );
};

export default SuperAdminUsers;
